import { useEffect, useMemo } from 'react';

import { useForm } from '@givebacks/formy';
import { phoneNumberDisplayFormat } from 'core/Utils';
import {
  AdditionalFieldOption,
  AdditionalFieldsFormsGuard,
  Card,
  Divider,
  Select,
  Stack,
  Text,
  TextInput,
} from 'ui';
import { isEmail, isNotEmpty, phoneNumberRegex } from 'ui/Form';

type MembershipUnitFormType = {
  member_type: string;
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  customOptions: { [string: string]: string };
};

const MEMBER_TYPE_NAMES = ['first_name', 'last_name', 'email', 'mobile', 'member_type'];

const MembershipUnitForm = ({
  options,
  index,
  onChange,
}: {
  options: AdditionalFieldOption[];
  index: number;
  onChange: (index: number, changes: MembershipUnitFormType) => void;
}) => {
  const otherOptions = useMemo(
    () => options.filter((option) => !MEMBER_TYPE_NAMES.includes(option.name)),
    [options]
  );

  const _options = useMemo(() => {
    const optionObject = {};
    if (!otherOptions) return optionObject;
    otherOptions.map((option) => (optionObject[option.name] = ''));
    return optionObject;
  }, [otherOptions]);

  const form = useForm<MembershipUnitFormType>(
    {
      initialValues: {
        customOptions: _options,
        member_type: '',
        first_name: '',
        last_name: '',
        email: '',
        mobile: '',
      },
      validate: {
        member_type: isNotEmpty('Member Type is required'),
        first_name: isNotEmpty('First Name is required'),
        last_name: isNotEmpty('Last Name is required'),
        email: (value, values) => {
          if (value) {
            return isEmail('Valid email is required')(value);
          } else if (values.member_type == 'Student') {
            return null;
          } else if (values.mobile) {
            return null;
          } else {
            return 'Email is required';
          }
        },
        mobile: (value, values) => {
          if (value) {
            return !phoneNumberRegex.test(String(value).replace(/\D/g, ''))
              ? 'Valid phone number is required'
              : null;
          } else if (values.member_type == 'Student') {
            return null;
          } else if (values.email) {
            return null;
          } else {
            return 'Phone Number is required';
          }
        },
      },
      validateInputOnBlur: true,
      onValuesChange: (values) => onChange(index, values),
      /** Add required attributes to  */
      enhanceGetInputProps: (payload) => {
        const isEmailOrPhoneNumberRequired = payload.form.values.member_type !== 'Student';
        if (payload.field === 'email') {
          return {
            required:
              isEmailOrPhoneNumberRequired &&
              (!payload.form.isDirty('mobile') || !payload.form.isValid('mobile')),
          };
        }
        if (payload.field === 'mobile') {
          return {
            required:
              isEmailOrPhoneNumberRequired &&
              (!payload.form.isDirty('email') || !payload.form.isValid('email')),
          };
        }

        return {};
      },
    },
    { id: `unit_${index}` }
  );

  useEffect(() => {
    // onChange of member_type to Student, we should clear errors
    if (form.values.member_type == 'Student') {
      form.clearFieldError('email');
      form.clearFieldError('mobile');
    }
    // if either field is dirty, we can clear errors and wait to validate
    else if (form.isDirty('email') || form.isDirty('mobile')) {
      form.clearFieldError('mobile');
      form.clearFieldError('email');
    }
  }, [form.values.member_type, form.values.email, form.values.mobile]);

  const memberType = options.find((option) => option.name == 'member_type');

  return (
    <Card
      radius="sm"
      style={(theme) => ({ border: `1px solid ${theme.colors.gray[1]}` })}
      ref={form.formRef}
    >
      <Stack>
        <Text fz="lg" fw={600}>
          Member {index + 1}
        </Text>
        <Divider />
        {memberType && (
          <Select
            label="Member Type"
            data={memberType?.options}
            required
            {...form.getInputProps('member_type')}
          />
        )}
        <Text fz="xs" c="gray">
          A valid email address OR mobile phone number (can be any 10 digits) is required for each
          non-student member.
        </Text>
        <TextInput label="First Name" {...form.getInputProps('first_name')} required />
        <TextInput label="Last Name" {...form.getInputProps('last_name')} required />
        <TextInput label="Email" {...form.getInputProps('email')} />
        <TextInput
          label="Phone Number"
          {...form.getInputProps('mobile')}
          onChange={(event) => {
            const formattedField = phoneNumberDisplayFormat(event.target.value);
            form.setFieldValue('mobile', formattedField);
          }}
        />
        <AdditionalFieldsFormsGuard
          options={otherOptions}
          onChange={(_otherOptions, values) => {
            form.setFieldValue('customOptions', values.asObject as { [string: string]: string });
          }}
        />
      </Stack>
    </Card>
  );
};

export { MembershipUnitForm };
export type { MembershipUnitFormType };
